import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  TvIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

interface Environment {
  id: string;
  label: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  badge: string;
  badgeColor: string;
  frameW: number;
  frameH: number;
}

const BASE_URL = window.location.origin;

const ENVIRONMENTS: Environment[] = [
  {
    id: 'desktop',
    label: 'Desktop',
    description: 'Full website as seen on laptops and large monitors',
    path: '/',
    icon: <ComputerDesktopIcon className="w-5 h-5" />,
    badge: 'Default',
    badgeColor: 'bg-blue-100 text-blue-700',
    frameW: 1280,
    frameH: 800,
  },
  {
    id: 'mobile',
    label: 'Mobile',
    description: 'Optimised mobile experience at /m',
    path: '/m',
    icon: <DevicePhoneMobileIcon className="w-5 h-5" />,
    badge: 'iOS / Android',
    badgeColor: 'bg-green-100 text-green-700',
    frameW: 390,
    frameH: 844,
  },
  {
    id: 'tablet',
    label: 'Tablet',
    description: 'Tablet-optimised layout at /t',
    path: '/t',
    icon: <DeviceTabletIcon className="w-5 h-5" />,
    badge: 'iPad',
    badgeColor: 'bg-amber-100 text-amber-700',
    frameW: 768,
    frameH: 1024,
  },
  {
    id: 'tv',
    label: 'TV Display',
    description: 'Fullscreen TV mode for smart TVs and displays at /tv',
    path: '/tv',
    icon: <TvIcon className="w-5 h-5" />,
    badge: 'Apple TV / Smart TV',
    badgeColor: 'bg-gray-100 text-gray-600',
    frameW: 1920,
    frameH: 1080,
  },
];

const AVAILABLE_WIDTH = 340;

interface EnvCardProps {
  env: Environment;
  isExpanded: boolean;
  onToggle: () => void;
}

function EnvCard({ env, isExpanded, onToggle }: EnvCardProps) {
  const fullUrl = `${BASE_URL}${env.path}`;

  const scale = AVAILABLE_WIDTH / env.frameW;
  const scaledHeight = Math.round(env.frameH * scale);

  const isMobileOrTablet = env.id === 'mobile' || env.id === 'tablet';
  const borderPx = isMobileOrTablet ? (env.id === 'mobile' ? 8 : 12) : 0;
  const innerW = env.frameW - borderPx * 2;
  const innerH = env.frameH - borderPx * 2;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3.5">
        <div className="w-10 h-10 rounded-xl bg-golf-sand flex items-center justify-center text-golf-orange flex-shrink-0">
          {env.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-bold text-golf-gray text-sm">{env.label}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${env.badgeColor}`}>
              {env.badge}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5 leading-snug">{env.description}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-golf-orange border border-golf-orange/30 bg-golf-orange/5 rounded-lg active:bg-golf-orange active:text-white transition-colors"
          >
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
            Open
          </a>
          <button
            onClick={onToggle}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 bg-gray-50 rounded-lg active:bg-gray-100 transition-colors"
          >
            {isExpanded ? 'Hide' : 'Preview'}
            <ChevronDownIcon
              className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            className="overflow-hidden border-t border-gray-100"
          >
            <div
              className="bg-gray-100 flex justify-center overflow-hidden"
              style={{ height: scaledHeight + 24 }}
            >
              <div className="mt-3" style={{ width: AVAILABLE_WIDTH, height: scaledHeight }}>
                <div
                  style={{
                    width: env.frameW,
                    height: env.frameH,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                  }}
                  className={`relative overflow-hidden shadow-xl bg-white ${
                    env.id === 'mobile'
                      ? 'rounded-[2.5rem] border-[8px] border-gray-800'
                      : env.id === 'tablet'
                      ? 'rounded-[1.5rem] border-[12px] border-gray-800'
                      : 'rounded-xl'
                  }`}
                >
                  {isMobileOrTablet && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 bg-gray-800 rounded-b-lg z-10" />
                  )}
                  <iframe
                    key={env.id}
                    src={fullUrl}
                    title={`${env.label} preview`}
                    width={innerW}
                    height={innerH}
                    className="border-0"
                    loading="lazy"
                    style={{ display: 'block' }}
                  />
                </div>
              </div>
            </div>

            <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center gap-2">
              <GlobeAltIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <code className="text-xs text-gray-500 truncate flex-1">{fullUrl}</code>
              <button
                onClick={() => navigator.clipboard.writeText(fullUrl)}
                className="flex-shrink-0 text-xs text-gray-400 active:text-golf-orange transition-colors"
              >
                Copy
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function MobilePreviewEnvironments() {
  const [expanded, setExpanded] = useState<string | null>(null);

  const toggle = (id: string) => setExpanded((prev) => (prev === id ? null : id));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-golf-gray leading-tight">Preview Environments</h2>
          <p className="text-sm text-gray-500 mt-1">
            Launch or preview the site across all supported device environments.
          </p>
        </div>
        <a
          href={BASE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-golf-orange text-white text-xs font-semibold rounded-xl shadow-sm active:bg-golf-orange/90 transition-colors"
        >
          <GlobeAltIcon className="w-3.5 h-3.5" />
          Open Live Site
        </a>
      </div>

      <div className="space-y-3">
        {ENVIRONMENTS.map((env) => (
          <EnvCard
            key={env.id}
            env={env}
            isExpanded={expanded === env.id}
            onToggle={() => toggle(env.id)}
          />
        ))}
      </div>

      <div className="bg-golf-sand/50 border border-golf-sand rounded-xl p-4">
        <p className="font-semibold text-golf-gray mb-2 text-sm">How previews work</p>
        <ul className="space-y-1.5 text-xs text-gray-500">
          {[
            'Previews load the live site inside an embedded frame — data is real and live',
            'Desktop and TV previews can be resized to common display resolutions',
            'Large resolutions are scaled down to fit — the actual iframe renders at full resolution',
            <>Use <strong className="text-golf-gray">Open</strong> to launch a full tab for hands-on testing or to share a link</>,
            'TV Display mode auto-rotates slides — press spacebar to pause when open in a tab',
          ].map((text, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
